import * as Dialog from "@radix-ui/react-dialog";
import { Cross2Icon } from "@radix-ui/react-icons";
import clsx from "clsx";
import React, { useState } from "react";
import { FormattedMessage } from "react-intl";

import { Button, type ButtonProps } from "./button";

export function ConfirmationDialog({
  title,
  description,
  actionText,
  onActionClick,
  children,
  contentClassName,
}: React.PropsWithChildren<{
  title: React.ReactChild;
  description: React.ReactChild;
  actionText: React.ReactChild;
  onActionClick(closeDialog: () => void): void;
  /** Widen/adjust the dialog panel for a body that needs more room — see `DialogContent`. */
  contentClassName?: string;
}>) {
  const [isOpen, setIsOpen] = useState(false);

  function closeDialog() {
    setIsOpen(false);
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
      <Dialog.Trigger asChild>{children}</Dialog.Trigger>

      <ConfirmationDialog.Content title={title} description={description} actionProps={{ onClick: () => onActionClick(closeDialog), children: actionText }} contentClassName={contentClassName} />
    </Dialog.Root>
  );
}

ConfirmationDialog.Content = DialogContent;
ConfirmationDialog.Close = DialogClose;

function DialogContent({
  title,
  description,
  actionProps,
  contentClassName,
}: {
  title: React.ReactNode;
  description: React.ReactNode;
  actionProps: ButtonProps;
  /** Opt-in override for the panel itself (e.g. a wider `sm:tw-max-w-*` when the body needs more room).
   *  REPLACES the default `sm:max-w-xl` rather than being appended to it: `important: true` makes every
   *  utility `!important`, so two competing max-widths have equal specificity and STYLESHEET order — not
   *  class order — decides, which silently ignored the override. Omitted callers are unchanged. */
  contentClassName?: string;
}) {
  return (
    <Dialog.Portal>
      <Dialog.Overlay
        className={clsx(
          // NOTE: tw-z-50 is not enough, page header has a higher z index
          "tw-fixed tw-inset-0 tw-z-[100] tw-bg-black/30 tw-backdrop-blur-sm",
          "data-[state=open]:tw-animate-in data-[state=open]:tw-fade-in-0",
          "data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0",
        )}
      />

      <Dialog.Content
        className={clsx(
          "tw-z-[100] tw-border tw-bg-white tw-p-6 tw-shadow-lg tw-duration-200",
          // The dialog fills a mobile screen. Capping the panel at 90vh and centring it left empty
          // bands above and below it on a phone while the body itself scrolled — the panel is the whole
          // task on a small screen, so it takes the whole screen. `inset-0` + no translate + no radius;
          // the body still scrolls inside it.
          "tw-fixed tw-inset-0 tw-w-full tw-translate-x-0 tw-translate-y-0 tw-overflow-y-auto tw-rounded-none",
          // From `sm` up, use a centred panel capped at 90vh. A taller panel would overflow above and below
          // the viewport, making its title and action buttons unreachable.
          "sm:tw-inset-auto sm:tw-left-1/2 sm:tw-top-1/2 sm:tw-max-h-[90vh] sm:-tw-translate-x-1/2 sm:-tw-translate-y-1/2 sm:tw-rounded-lg",
          // NOTE: a scrolling `overflow-y` makes a computed `overflow-x: visible` become `auto` (CSS
          // Overflow L3 §3), so nothing inside may rely on painting past the panel's left/right edge.
          contentClassName ?? "sm:tw-max-w-xl",
          "data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out",
          "data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0",
          // FIXME: these don't work for whatever reason
          "data-[state=closed]:tw-zoom-out-95 data-[state=open]:tw-zoom-in-95",
          "data-[state=closed]:tw-slide-out-to-left-1/2 data-[state=open]:tw-slide-in-from-left-1/2",
          "data-[state=closed]:tw-slide-out-to-top-1/2 data-[state=open]:tw-slide-in-from-top-1/2",
        )}
      >
        <div className="tw-space-y-6">
          <Dialog.Title className="tw-m-0 tw-text-xl tw-font-semibold">{title}</Dialog.Title>

          <Dialog.Description className="tw-m-0 tw-space-y-2 tw-leading-relaxed">{description}</Dialog.Description>

          <div className="tw-flex tw-gap-4">
            <Button {...actionProps} variant="primary" rounded size="1.5xl" className="tw-flex-1" />

            <Dialog.Close asChild>
              <Button variant="outline" rounded size="1.5xl" className="tw-flex-1">
                <FormattedMessage id="events.watch_modal.cancel" />
              </Button>
            </Dialog.Close>
          </div>
        </div>

        <ConfirmationDialog.Close />
      </Dialog.Content>
    </Dialog.Portal>
  );
}
DialogContent.displayName = "ConfirmationDialog.Content";

export type ConfirmationDialogCloseProps = {
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  outline?: boolean;
};

function DialogClose({ onClick, outline = true }: ConfirmationDialogCloseProps) {
  return (
    <Dialog.Close
      onClick={onClick}
      className={clsx("tw-absolute tw-right-4 tw-top-4 tw-rounded-sm", "tw-opacity-70 tw-transition-opacity hover:tw-opacity-100", "tw-h-5 tw-w-5 tw-border-0 tw-bg-transparent tw-p-0", {
        "tw-outline-none": !outline,
      })}
    >
      <Cross2Icon className="tw-h-5 tw-w-5" />
      <span className="tw-sr-only">
        <FormattedMessage id="alert.close" />
      </span>
    </Dialog.Close>
  );
}
DialogContent.displayName = "ConfirmationDialog.Close";
